import type { Node } from "@xyflow/react";
import { formatNZD } from "../../utils/formatters";
import { getNodeColorScheme } from "./graphUtils";
import type { CalculationNodeData } from "./graphUtils";
import styles from "./ImpactTracePanel.module.css";

export interface ImpactTracePanelProps {
  trace: Node<CalculationNodeData>[];
  selectedNodeId: string;
  onClose: () => void;
}

export function ImpactTracePanel({
  trace,
  selectedNodeId,
  onClose,
}: ImpactTracePanelProps) {
  return (
    <aside className={styles.panel} aria-label="Impact trace">
      <div className={styles.header}>
        <h3 className={styles.title}>Impact Trace</h3>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close panel"
        >
          &times;
        </button>
      </div>

      <ol className={styles.traceList}>
        {trace.map((node, index) => {
          const isSelected = node.id === selectedNodeId;
          const color = getNodeColorScheme(
            node.data.nodeClass,
            node.data.nodeType,
            node.data.label,
          );

          return (
            <li
              key={node.id}
              className={`${styles.traceItem} ${isSelected ? styles.selected : ""}`}
            >
              <div className={styles.stepHeader}>
                <span className={styles.stepNumber}>{index + 1}</span>
                <span
                  className={styles.classBadge}
                  style={{
                    backgroundColor: color.background,
                    borderColor: color.border,
                    color: color.text,
                  }}
                >
                  {node.data.displayCategory}
                </span>
              </div>

              <div className={styles.stepBody}>
                <span className={styles.nodeLabel}>{node.data.label}</span>
                <span className={styles.amount}>
                  {formatNZD(node.data.calculatedAmount)}
                </span>
              </div>

              {node.data.calculationDescription && (
                <p className={styles.formula}>
                  {node.data.calculationDescription}
                </p>
              )}

              {node.data.hasVariance && node.data.varianceAmount !== null && (
                <span className={styles.variance}>
                  {formatNZD(node.data.varianceAmount)} variance
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </aside>
  );
}
